/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { z } from 'zod';

// ============================================
// Velocity Sync Settings
// ============================================

/**
 * Auto-save settings for velocity mode
 * Controls automatic commit and push behavior
 */
export const AutoSaveConfigSchema = z.object({
  /** Whether auto-save is enabled */
  enabled: z.boolean().default(true),
  /** Automatically commit changes when detected */
  commitOnChange: z.boolean().default(true),
  /** Automatically push after committing */
  pushAfterCommit: z.boolean().default(true),
});

export const VelocitySyncSettingsSchema = z.object({
  /**
   * Sync mode determines where changes are synced:
   * - 'velocity-mode': Sync with remote velocity branch only (Google Docs-like experience)
   * - 'main-sync': Sync velocity branch with main/master (legacy behavior)
   * Default is 'velocity-mode' for seamless auto-save experience
   */
  syncMode: z.enum(['velocity-mode', 'main-sync']).default('velocity-mode'),
  /** Auto-sync interval in minutes (1-60) */
  autoSyncInterval: z.number().min(1).max(60).default(10),
  /** Sync strategy for main-sync mode */
  syncStrategy: z.enum(['merge', 'rebase', 'reset']).default('merge'),
  /** Enable semantic merge for conflict resolution */
  enableSemanticMerge: z.boolean().default(true),
  /** Auto-save settings for velocity mode */
  autoSave: AutoSaveConfigSchema.optional(),
});

// ============================================
// Push to Main Settings (velocity → main)
// ============================================

export const AutoMergeConfigSchema = z.object({
  enabled: z.boolean().default(true),
  allowedPaths: z.array(z.string()).default(['data/product/**']),
  excludedPaths: z.array(z.string()).default([]),
  requireCleanMerge: z.boolean().default(true),
  notifyOnMerge: z.boolean().default(false),
});

export const AutoPRConfigSchema = z.object({
  enabled: z.boolean().default(false),
  schedule: z.string().default('0 2 * * *'), // Cron: daily at 2 AM
  prTitleTemplate: z.string().default('Product Data Sync - {{date}}'),
  prBodyTemplate: z.string().default('Automated sync of product data changes from velocity branch.'),
  autoMerge: z.boolean().default(true),
  requiredReviewers: z.array(z.string()).default([]),
  labels: z.array(z.string()).default(['product-data', 'auto-sync']),
});

export const HybridConfigSchema = z.object({
  autoMergeIfSafe: z.boolean().default(true),
  maxChangesForAutoMerge: z.number().default(50),
  fallbackToPR: z.boolean().default(true),
});

export const EventTriggeredConfigSchema = z.object({
  enabled: z.boolean().default(false),
  triggers: z.object({
    onRelease: z.boolean().default(false),
    onSprintEnd: z.boolean().default(false),
    onEpicComplete: z.boolean().default(false),
    onManualCheckpoint: z.boolean().default(true),
    onChangeThreshold: z.number().default(0), // 0 = disabled
  }).default({}),
  autoMerge: z.boolean().default(false),
  requiredReviewers: z.array(z.string()).default([]),
});

export const PushToMainSettingsSchema = z.object({
  strategy: z.enum(['auto-merge', 'auto-pr', 'event-triggered', 'hybrid', 'manual']).default('hybrid'),
  autoMerge: AutoMergeConfigSchema.optional(),
  autoPR: AutoPRConfigSchema.optional(),
  hybrid: HybridConfigSchema.optional(),
  eventTriggered: EventTriggeredConfigSchema.optional(),
});

// ============================================
// Combined Velocity Settings
// ============================================

export const VelocitySettingsSchema = z.object({
  sync: VelocitySyncSettingsSchema.default({}),
  pushToMain: PushToMainSettingsSchema.default({}),
  /** Whether to show the Dashboard in the navigation (default: false) */
  showDashboard: z.boolean().default(false),
  /**
   * Simple mode for Changes page (default: true)
   * When enabled, Velocity does NOT auto-save, auto-commit, or auto-push.
   * The user initiates all actions with respect to source control.
   * No background syncing or automatic git behavior.
   */
  simpleMode: z.boolean().default(true),
});

// ============================================
// Repository Settings
// ============================================

export const RepositorySettingsSchema = z.object({
  defaultBranch: z.string().default('main'),
  productDataPath: z.string().default('data/product'),
  velocityBranchName: z.string().default('velocity'),
});

// ============================================
// AI Settings
// ============================================

export const AISettingsSchema = z.object({
  enableAIResolution: z.boolean().default(true),
  autoResolveThreshold: z.number().min(0).max(1).default(0.9),
  preferredModel: z.string().default('claude-4-5-sonnet'),
});

// ============================================
// Access Control Settings
// ============================================

export const AccessControlSettingsSchema = z.object({
  adminUsers: z.array(z.string()).default([]), // User IDs with admin access
  syncApprovers: z.array(z.string()).default([]), // User IDs who can approve syncs
});

// ============================================
// Integration Settings (GitHub, GitLab, Jira, Linear, Slack)
// ============================================

export const GitHubIntegrationSchema = z.object({
  enabled: z.boolean().default(false),
  accessToken: z.string().optional(),
  webhookSecret: z.string().optional(),
});

export const SlackIntegrationSchema = z.object({
  enabled: z.boolean().default(false),
  webhookUrl: z.string().optional(),
  channelId: z.string().optional(),
  notifyOnSync: z.boolean().default(false),
  notifyOnConflict: z.boolean().default(true),
});

/**
 * GCP Project settings
 * Shared GCP project configuration that can be used across integrations.
 * This serves as the default GCP project for services like Vertex AI and GCS.
 */
export const GCPProjectOrgSettingsSchema = z.object({
  /** Default GCP project ID used for Google Cloud services */
  projectId: z.string().default(''),
  /** Default region for GCP services */
  region: z.string().default('us-central1'),
});

/**
 * Vertex AI Anthropic integration settings
 * When enabled, Anthropic models will be accessed via Google Cloud Vertex AI
 * instead of the direct Anthropic API
 */
export const VertexAnthropicOrgSettingsSchema = z.object({
  /** Whether to use Vertex AI for Anthropic models (default: false) */
  enabled: z.boolean().default(false),
  /** Google Cloud Project ID for Vertex AI (if empty, uses gcpProject.projectId) */
  projectId: z.string().default(''),
  /** Vertex AI region (e.g., 'global', 'us-east1', 'europe-west1') */
  region: z.string().default('global'),
});

/**
 * Google Cloud Storage integration settings
 * Used for GCS as a data source for Playbooks/Knowledge Mining
 */
export const GCSIntegrationOrgSettingsSchema = z.object({
  /** Whether GCS integration is enabled */
  enabled: z.boolean().default(false),
  /** GCP project ID for GCS (if empty, uses gcpProject.projectId) */
  projectId: z.string().default(''),
  /** Default GCS bucket name for playbook data sources */
  bucketName: z.string().default(''),
  /** Optional bucket prefix/folder path */
  bucketPrefix: z.string().default(''),
});

/**
 * DSA (Distributed System Agents) organization-level settings
 * Controls whether DSA is available for the organization and how users can participate
 */
export const DSAOrgSettingsSchema = z.object({
  /** Whether DSA is enabled for the organization (default: false) */
  enabled: z.boolean().default(false),
  /** 
   * Whether individual users can opt into DSA participation (default: true)
   * If false, DSA is controlled entirely at the org level - users cannot enable/disable it themselves.
   * If true, users can choose whether to participate when DSA is enabled org-wide.
   */
  allowUserParticipation: z.boolean().default(true),
  /** 
   * Velocity repo URL for DSA coordination (optional)
   * This is set after the admin creates or detects the Velocity repository.
   * If not set, users will see a message to contact admin to configure DSA.
   */
  velocityRepoUrl: z.string().optional(),
  /** Default poll interval for DSA workers in milliseconds (default: 5 minutes) */
  defaultPollIntervalMs: z.number().default(300000),
  /** Minimum eligibility score for agents to run (0.0-1.0, default: 0.6) */
  defaultMinimumScore: z.number().min(0).max(1).default(0.6),
});

/**
 * SharePoint integration settings
 * Uses Microsoft Graph API with OAuth2 client credentials for accessing SharePoint resources.
 * Requires an Azure AD app registration with Sites.Read.All (or Sites.ReadWrite.All) permission.
 */
export const SharePointOrgSettingsSchema = z.object({
  /** Whether SharePoint integration is enabled */
  enabled: z.boolean().default(false),
  /** Azure AD tenant ID (directory ID) */
  tenantId: z.string().default(''),
  /** Azure AD application (client) ID */
  clientId: z.string().default(''),
  /** Azure AD client secret */
  clientSecret: z.string().default(''),
  /** SharePoint site URL (e.g., https://contoso.sharepoint.com/sites/engineering) */
  siteUrl: z.string().default(''),
});

export const OrganizationIntegrationSettingsSchema = z.object({
  github: GitHubIntegrationSchema.optional(),
  slack: SlackIntegrationSchema.optional(),
  /** Shared GCP project configuration */
  gcpProject: GCPProjectOrgSettingsSchema.optional(),
  vertexAnthropic: VertexAnthropicOrgSettingsSchema.optional(),
  /** GCS integration for playbook data sources */
  gcs: GCSIntegrationOrgSettingsSchema.optional(),
  /** DSA (Distributed System Agents) organization settings */
  dsa: DSAOrgSettingsSchema.optional(),
  /** SharePoint integration for document access */
  sharepoint: SharePointOrgSettingsSchema.optional(),
});

// ============================================
// Complete Organization Settings
// ============================================

export const OrganizationSettingsDataSchema = z.object({
  velocitySettings: VelocitySettingsSchema.optional(),
  repositorySettings: RepositorySettingsSchema.optional(),
  integrationSettings: OrganizationIntegrationSettingsSchema.optional(),
  aiSettings: AISettingsSchema.optional(),
  accessControlSettings: AccessControlSettingsSchema.optional(),
});

// ============================================
// Request/Response Schemas
// ============================================

export const UpdateOrganizationSettingsCategorySchema = z.object({
  category: z.enum(['velocitySettings', 'repositorySettings', 'integrationSettings', 'aiSettings', 'accessControlSettings']),
  settings: z.any(), // Will be validated based on category
});

// ============================================
// Type Exports
// ============================================

export type VelocitySyncSettings = z.infer<typeof VelocitySyncSettingsSchema>;
export type AutoSaveConfig = z.infer<typeof AutoSaveConfigSchema>;
export type AutoMergeConfig = z.infer<typeof AutoMergeConfigSchema>;
export type AutoPRConfig = z.infer<typeof AutoPRConfigSchema>;
export type HybridConfig = z.infer<typeof HybridConfigSchema>;
export type EventTriggeredConfig = z.infer<typeof EventTriggeredConfigSchema>;
export type PushToMainSettings = z.infer<typeof PushToMainSettingsSchema>;
export type VelocitySettings = z.infer<typeof VelocitySettingsSchema>;
export type RepositorySettings = z.infer<typeof RepositorySettingsSchema>;
export type AISettings = z.infer<typeof AISettingsSchema>;
export type AccessControlSettings = z.infer<typeof AccessControlSettingsSchema>;
export type GitHubIntegration = z.infer<typeof GitHubIntegrationSchema>;
export type SlackIntegration = z.infer<typeof SlackIntegrationSchema>;
export type GCPProjectOrgSettings = z.infer<typeof GCPProjectOrgSettingsSchema>;
export type VertexAnthropicOrgSettings = z.infer<typeof VertexAnthropicOrgSettingsSchema>;
export type GCSIntegrationOrgSettings = z.infer<typeof GCSIntegrationOrgSettingsSchema>;
export type DSAOrgSettings = z.infer<typeof DSAOrgSettingsSchema>;
export type SharePointOrgSettings = z.infer<typeof SharePointOrgSettingsSchema>;
export type OrganizationIntegrationSettings = z.infer<typeof OrganizationIntegrationSettingsSchema>;
export type OrganizationSettingsData = z.infer<typeof OrganizationSettingsDataSchema>;
export type UpdateOrganizationSettingsCategoryRequest = z.infer<typeof UpdateOrganizationSettingsCategorySchema>;

// Category type for API routes
export type OrganizationSettingsCategory = 'velocitySettings' | 'repositorySettings' | 'integrationSettings' | 'aiSettings' | 'accessControlSettings';

// ============================================
// Default Values
// ============================================

export const DEFAULT_ORGANIZATION_SETTINGS: OrganizationSettingsData = {
  velocitySettings: {
    sync: {
      syncMode: 'velocity-mode', // Default to velocity mode
      autoSyncInterval: 10, // 10 minutes (only used when simpleMode is false)
      syncStrategy: 'merge',
      enableSemanticMerge: true,
      autoSave: {
        enabled: false, // Disabled by default (Simple mode)
        commitOnChange: false,
        pushAfterCommit: false,
      },
    },
    pushToMain: {
      strategy: 'hybrid',
      hybrid: {
        autoMergeIfSafe: true,
        maxChangesForAutoMerge: 50,
        fallbackToPR: true,
      },
    },
    showDashboard: false, // Dashboard is hidden by default
    simpleMode: true, // Simple mode is default - no auto-save, auto-commit, or auto-push
  },
  repositorySettings: {
    defaultBranch: 'main',
    productDataPath: 'data/product',
    velocityBranchName: 'velocity',
  },
  integrationSettings: {
    dsa: {
      enabled: false, // DSA is disabled by default at org level
      allowUserParticipation: true, // When enabled, users can opt in/out
      defaultPollIntervalMs: 300000, // 5 minutes
      defaultMinimumScore: 0.6,
    },
  },
  aiSettings: {
    enableAIResolution: true,
    autoResolveThreshold: 0.9,
    preferredModel: 'claude-sonnet',
  },
  accessControlSettings: {
    adminUsers: [], // First org creator becomes admin
    syncApprovers: [], // Empty = no approval required
  },
};
